import { useEffect } from 'react'

// Bubble map (S10): proportional circles at region centroids sized by a chosen
// metric — a standard GIS complement to the choropleth. Radius is
// area-proportional (∝ √value) so perceived size matches magnitude. The metric
// value is precomputed per feature into a points source; the circle layer reads
// it via a zoom-scaled interpolation of the normalized radius.
export const BUBBLE_METRICS = [
  { key: 'population', label: 'Population', prop: 'population' },
  { key: 'gdpPerCapita', label: 'PIB / habitant', compute: (p) => (p.country_total_population > 0 ? (p.country_gdp || 0) / p.country_total_population : 0) },
  { key: 'area', label: 'Superficie', prop: 'area_km2' },
  { key: 'structures', label: 'Structures (à moi)', prop: 'stat_struct_sel' },
  { key: 'housing', label: 'Logement (à moi)', prop: 'stat_housing_capacity' },
  { key: 'electricity', label: 'Production élec. (à moi)', prop: 'stat_elec_production' },
]

const MAX_RADIUS = 34

export function useBubbleMap(map, { showBubbles, bubbleMetric, bubbleColor, bubbleSize, regionsData }) {
  useEffect(() => {
    if (!map.current || !map.current.getLayer('regions-fill')) return
    const color = bubbleColor || '#4FD0E0'
    const sizeMul = bubbleSize || 1

    const metric = BUBBLE_METRICS.find((m) => m.key === bubbleMetric) || BUBBLE_METRICS[0]
    const valueOf = (p) => {
      const v = metric.compute ? metric.compute(p) : Number(p[metric.prop] || 0)
      return Number.isFinite(v) ? v : 0
    }

    let fc = { type: 'FeatureCollection', features: [] }
    if (showBubbles && regionsData?.features?.length) {
      let max = 0
      const pts = []
      for (const f of regionsData.features) {
        const c = f.properties._centroid
        if (!c) continue
        const v = valueOf(f.properties)
        if (v <= 0) continue
        if (v > max) max = v
        pts.push({ c, v, name: f.properties.name, region_id: f.properties.region_id })
      }
      fc = {
        type: 'FeatureCollection',
        features: pts.map((pt) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: pt.c },
          // normalized radius 0..1 (√ for area-proportional perception)
          properties: { r: max > 0 ? Math.sqrt(pt.v / max) : 0, v: pt.v, name: pt.name, region_id: pt.region_id },
        })),
      }
    }

    const src = map.current.getSource('bubble-map')
    if (!src) {
      map.current.addSource('bubble-map', { type: 'geojson', data: fc })
      map.current.addLayer({
        id: 'bubble-map',
        type: 'circle',
        source: 'bubble-map',
        paint: {
          // Zoom must be the TOP-LEVEL interpolate input; the feature-data
          // (normalized radius `r`) lives in the outputs. Nesting the zoom
          // interpolate inside an arithmetic expression is invalid in MapLibre
          // and makes addLayer throw (which is why bubbles never rendered).
          'circle-radius': [
            'interpolate', ['linear'], ['zoom'],
            2, ['*', ['get', 'r'], MAX_RADIUS * 0.5 * sizeMul],
            6, ['*', ['get', 'r'], MAX_RADIUS * sizeMul],
          ],
          'circle-color': color,
          'circle-opacity': 0.55,
          'circle-stroke-color': '#1a1a1a',
          'circle-stroke-width': 1,
        },
      })
    } else {
      src.setData(fc)
    }
    if (map.current.getLayer('bubble-map')) {
      // Re-apply colour/size on every run so live edits take effect.
      map.current.setPaintProperty('bubble-map', 'circle-color', color)
      map.current.setPaintProperty('bubble-map', 'circle-radius', [
        'interpolate', ['linear'], ['zoom'],
        2, ['*', ['get', 'r'], MAX_RADIUS * 0.5 * sizeMul],
        6, ['*', ['get', 'r'], MAX_RADIUS * sizeMul],
      ])
      map.current.setLayoutProperty('bubble-map', 'visibility', showBubbles ? 'visible' : 'none')
    }
  }, [map, showBubbles, bubbleMetric, bubbleColor, bubbleSize, regionsData])
}
